import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import RadialMenu from './RadialMenu';
import './AdminDashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const rawName = user?.name || user?.email?.split('@')[0] || 'Admin';
  const adminName = rawName.charAt(0).toUpperCase() + rawName.slice(1).toLowerCase();

  const getTimeInfo = () => {
    const hour = currentTime.getHours();
    let greeting = 'Good night';
    let icon = '🌙'; // Using as a reference for the 'aura' color logic
    let theme = 'night';

    if (hour >= 5 && hour < 12) {
        greeting = 'Good morning';
        theme = 'morning';
    } else if (hour >= 12 && hour < 17) {
        greeting = 'Good afternoon';
        theme = 'afternoon';
    } else if (hour >= 17 && hour < 21) {
        greeting = 'Good evening';
        theme = 'evening';
    }

    return { greeting, theme };
  };

  const { greeting, theme } = getTimeInfo();
  const timeString = currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const dateString = currentTime.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div className={`admin-dashboard theme-${theme}`}>
      <div className="animated-gradient-bg" />

      {/* Ribbon / Wave Background */}
      <div className="admin-waves">
        <div className="admin-wave admin-wave-top"></div>
        <div className="admin-wave admin-wave-middle"></div>
        <div className="admin-wave admin-wave-bottom"></div>
      </div>

      <div className="dashboard-scroll">
        <div className="dashboard-content">
          
          {/* Top Bar / Status */}
          <motion.div 
            className="dashboard-top-bar"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="status-badge">
              <span className="status-dot"></span>
              System Active
            </div>
            <div className="live-clock">
              <span className="time">{timeString}</span>
              <span className="date">{dateString}</span>
            </div>
          </motion.div>

          <motion.img 
            src="/DarkLogo.png" 
            alt="METATIP" 
            className="dashboard-logo-premium"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
          />

          {/* Hero Greeting Section */}
          <div className="hero-greeting">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <h2 className="greeting-pre">{greeting},</h2>
              <h1 className="greeting-main-title">
                {adminName}<span className="shimmer-dot">.</span>
              </h1>
            </motion.div>
            
            <motion.p 
              className="greeting-sub"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 1 }}
            >
              Your digital anatomy workspace is ready. What are we building today?
            </motion.p>
          </div>

          {/* Refined Navigation Cards */}
          <div className="nav-grid">
            <motion.div 
              className="nav-card glass-card"
              onClick={() => navigate('/admin/organs')}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              whileHover={{ scale: 1.02, translateY: -5 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="card-glow" />
              <div className="card-icon-wrap">
                <img src="https://cdn-icons-png.flaticon.com/512/4785/4785728.png" alt="" />
              </div>
              <div className="card-text">
                <h3>Organs Gallery</h3>
                <p>Interact with 3D models</p>
              </div>
              <div className="card-arrow">→</div>
            </motion.div>

            <motion.div 
              className="nav-card glass-card"
              onClick={() => navigate('/admin/codes')}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7, duration: 0.5 }}
              whileHover={{ scale: 1.02, translateY: -5 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="card-glow" />
              <div className="card-icon-wrap">
                <img src="https://cdn-icons-png.flaticon.com/512/16136/16136332.png" alt="" />
              </div>
              <div className="card-text">
                <h3>Access Control</h3>
                <p>Manage security codes</p>
              </div>
              <div className="card-arrow">→</div>
            </motion.div>
          </div>

          {/* Quick Stats / Footer info */}
          <motion.div 
            className="dashboard-footer-hint"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
          >
            Drag the menu button to reposition your navigation
          </motion.div>
        </div>
      </div>

      <RadialMenu />
    </div>
  );
}

export default AdminDashboard;
